import React, {forwardRef, PropsWithChildren} from 'react';
import styled, {css} from 'styled-components';

import {hoverSelector} from '../../../../libs/shared/core/src/helpers/browser/hoverHelpers';
import {buildInteractionIdProps} from '../../../../libs/shared/core/src/helpers/interaction/interactionHelpers';
import {
  ClickEventHandler,
  FocusEventHandler,
  MouseEventHandler,
  Renderer,
} from '../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {ChromelessButton} from '../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon, IconName} from '../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisuallyHidden} from '../../../../libs/shared/ui/src/components/visuallyHidden/visuallyHidden';
import {topBarTooltipCustomMargin, topBarTooltipPreventOverflowPadding} from './topBarHelpers';

/*
 * Props.
 */

interface TopBarButtonProps {
  iconName: IconName;
  iconColor?: string;
  isSelected?: boolean;
  isDisabled?: boolean;
  hasHover?: boolean;
  onMouseDown?: MouseEventHandler;
  onClick?: ClickEventHandler;
  onFocus?: FocusEventHandler;
  onBlur?: FocusEventHandler;
  shouldAllowClickWhenSelected?: boolean;
  renderTooltip?: Renderer;
  className?: string;
  interactionId?: string;
}

/*
 * Constants.
 */

export const topBarButtonPadding = 6;
const iconSize = 20;
export const topBarButtonSize = topBarButtonPadding * 2 + iconSize;

/*
 * Styles.
 */

const StyledTooltipCoordinator = styled(TooltipCoordinator)`
  flex: none;
  align-self: center;
`;

const StyledButton = styled(ChromelessButton)`
  // Disable drag in Electron.
  -webkit-app-region: no-drag;

  border-radius: 8px;
`;

interface AppSwitcherButtonStyleProps {
  $isSelected?: boolean;
  $isDisabled?: boolean;
}

const StyledButtonContentDiv = styled.div<AppSwitcherButtonStyleProps>`
  padding: ${topBarButtonPadding}px;
  border-radius: 8px;
  ${(p) => addConditionalStyle(p)};
`;

function addConditionalStyle(props: AppSwitcherButtonStyleProps) {
  if (props.$isSelected) {
    return css`
      color: ${(p) => p.theme.topBar.buttonSelectedColor};
      background-color: ${(p) => p.theme.topBar.buttonSelectedBackground};
    `;
  }

  if (props.$isDisabled) {
    return css`
      color: ${(p) => p.theme.topBar.buttonDisabledColor};
    `;
  }

  return css`
    color: ${(p) => p.theme.topBar.buttonColor};

    ${hoverSelector}, *:focus && {
      color: ${(p) => p.theme.topBar.buttonHoverColor};
      background-color: ${(p) => p.theme.topBar.buttonHoverBackground};
    }
  `;
}

/*
 * Component.
 */

export const TopBarButton = forwardRef<HTMLDivElement, PropsWithChildren<TopBarButtonProps>>((props, ref) => {
  const onClick: ClickEventHandler = (event) => {
    if (props.isSelected && !props.shouldAllowClickWhenSelected) {
      return;
    }

    if (!props.onClick) {
      return;
    }

    props.onClick(event);
  };

  const onDoubleClick: MouseEventHandler = (event) => {
    // Don't maximize in Electron.
    event.preventDefault();
  };

  const interactionIdProps = props.interactionId ? buildInteractionIdProps(props.interactionId) : {};

  return (
    <StyledTooltipCoordinator
      render={props.renderTooltip}
      customMargin={topBarTooltipCustomMargin}
      preventOverflowPadding={topBarTooltipPreventOverflowPadding}
      hideOnMouseDown
      className={props.className}
    >
      <StyledButton
        ref={ref}
        hasHover={props.hasHover}
        shouldPreventMouseDown
        onMouseDown={props.onMouseDown}
        onClick={onClick}
        onDoubleClick={onDoubleClick}
        onFocus={props.onFocus}
        onBlur={props.onBlur}
        isDisabled={props.isDisabled}
      >
        <StyledButtonContentDiv
          $isDisabled={props.isDisabled}
          $isSelected={props.isSelected}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...interactionIdProps}
        >
          <Icon name={props.iconName} color={props.iconColor} />
          <VisuallyHidden>{props.children}</VisuallyHidden>
        </StyledButtonContentDiv>
      </StyledButton>
    </StyledTooltipCoordinator>
  );
});
